// src/components/admin/payroll/PayrollRunEnhanced.js
// Enhanced Payroll Run component for 1000+ employees
// Features: Summary dashboard, bulk actions, virtualized list, async processing
// With LOP, partial days, OT, incentives, and attendance validation

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Calendar, ArrowLeft, Lock, Unlock, RefreshCw,
    ChevronLeft, ChevronRight, Settings, Clock, Gift, FileText
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useEmployees } from '../../../contexts/EmployeeContext';
import domainService from '../../../services/domain.service';
import payrollService from '../../../services/payroll.service';

// New enhanced components
import PayrollSummaryDashboard from './components/PayrollSummaryDashboard';
import BulkActionToolbar from './components/BulkActionToolbar';
import VirtualizedEmployeeList from './components/VirtualizedEmployeeList';
import ProcessingProgress from './components/ProcessingProgress';
import AttendanceValidationModal from './components/AttendanceValidationModal';
import OTIncentivePanel from './components/OTIncentivePanel';

// Custom hooks

import usePayrollEmployees from './hooks/usePayrollEmployees';
import usePayrollProcessing from './hooks/usePayrollProcessing';







    import useWorkingDays from './hooks/useWorkingDays';
import useSalaryCalculations from './hooks/useSalaryCalculations';

// Import existing validation wizard for attendance validation
import AttendanceValidationWizard from './AttendanceValidationWizard';
import Payslip from './Payslip';

const PayrollRunEnhanced = () => {
    const { theme } = useTheme();
    const { employees: allEmployees } = useEmployees();
    const navigate = useNavigate();

    // Period state
    const [payrollPeriods, setPayrollPeriods] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState('');
    const [selectedPeriod, setSelectedPeriod] = useState(null);

    // View state
    const [activeCategory, setActiveCategory] = useState(null); // 'autoValidated', 'needsReview', 'hasIssues'
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [viewingEmployee, setViewingEmployee] = useState(null);

    // Modal state
    const [showPayslipModal, setShowPayslipModal] = useState(false);
    const [showProcessingModal, setShowProcessingModal] = useState(false);
    const [showAttendanceWizard, setShowAttendanceWizard] = useState(false);
    const [showAttendanceValidation, setShowAttendanceValidation] = useState(false);
    const [showOTPanel, setShowOTPanel] = useState(false);

    // OT and Incentives state
    const [otConfig, setOTConfig] = useState({
        enabled: false,
        rates: { regular: 1.5, holiday: 2.0, weekly_off: 2.0 },
        threshold: 8,
        calculation_base: 'basic'
    });
    const [incentives, setIncentives] = useState({});
    const [attendanceDecisions, setAttendanceDecisions] = useState({});

    // Processing hook
    const {
        isProcessing,
        jobId,
        progress,
        error: processingError,
        startProcessing,
        bulkValidate,
        lockPeriod,
        reset: resetProcessing
    } = usePayrollProcessing(selectedMonth);

    // Employees hook (pagination, filtering)
    const {
        employees,
        pagination,
        filters,
        loading: employeesLoading,
        error: employeesError,
        setPage,
        setFilters,
        setSearch,
        refetch: refetchEmployees
    } = usePayrollEmployees(selectedMonth, {
        status: activeCategory || 'all'
    });

    // Working days hook - calculates working days, holidays, LOP
    const {
        monthData: workingDaysData,
        holidays,
        calculateLOP,
        calculateProRata,
        loading: workingDaysLoading
    } = useWorkingDays(selectedMonth);

    // Salary calculations hook - PF, PT, ESI, OT, incentives
    const {
        calculatePF,
        calculatePT,
        calculateESI,
        calculateOTPay,
        calculateIncentives,
        calculateNetSalary,
        numberToWords
    } = useSalaryCalculations({ otConfig });

    // Generate payroll periods
    useEffect(() => {
        const fetchDomainAndGeneratePeriods = async () => {
            try {
                const domainInfo = await domainService.getDomainInfo();
                const registrationDate = domainInfo?.created_at
                    ? new Date(domainInfo.created_at)
                    : new Date(new Date().getFullYear(), 0, 1);

                const periods = generatePayrollPeriods(registrationDate);
                setPayrollPeriods(periods);

                // Set default to current month
                const currentMonth = new Date().getMonth();
                const currentYear = new Date().getFullYear();
                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const defaultPeriod = `${monthNames[currentMonth]} ${currentYear}`;
                setSelectedMonth(defaultPeriod);
            } catch (err) {
                console.error('Error fetching domain info:', err);
                // Fallback to current year
                const periods = generatePayrollPeriods(new Date(new Date().getFullYear(), 0, 1));
                setPayrollPeriods(periods);
            }
        };

        fetchDomainAndGeneratePeriods();
    }, []);

    // Update selected period when month changes
    useEffect(() => {
        const period = payrollPeriods.find(p => p.month === selectedMonth);
        setSelectedPeriod(period);
        setSelectedEmployeeIds([]);
        setActiveCategory(null);
    }, [selectedMonth, payrollPeriods]);

    // Update filters when category changes
    useEffect(() => {
        setFilters({ status: activeCategory || 'all' });
        setSelectedEmployeeIds([]);
    }, [activeCategory, setFilters]);

    // Generate periods helper
    const generatePayrollPeriods = (registrationDate) => {
        const periods = [];
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const monthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        const startDate = new Date(registrationDate);
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 2);

        const currentDate = new Date();
        let iterDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

        while (iterDate <= endDate) {
            const year = iterDate.getFullYear();
            const month = iterDate.getMonth();
            const monthName = monthNames[month];

            let status = 'upcoming';
            let processed = false;
            let locked = false;

            if (year < currentDate.getFullYear() ||
                (year === currentDate.getFullYear() && month < currentDate.getMonth())) {
                status = 'locked';
                processed = true;
                locked = true;
            } else if (year === currentDate.getFullYear() && month === currentDate.getMonth()) {
                status = 'current';
            }

            periods.push({
                month: `${monthName} ${year}`,
                range: `${monthName.toUpperCase()} 01-${monthName.toUpperCase()} ${monthDays[month]}`,
                status,
                processed,
                locked
            });

            iterDate.setMonth(iterDate.getMonth() + 1);
        }

        return periods;
    };

    // Handle category click from dashboard
    const handleCategoryClick = useCallback((category) => {
        setActiveCategory(category);
    }, []);

    // Handle process all
    const handleProcessAll = useCallback(async () => {
        const newJobId = await startProcessing(50);
        if (newJobId) {
            setShowProcessingModal(true);
        }
    }, [startProcessing]);

    // Handle processing complete
    const handleProcessingComplete = useCallback((job) => {
        // Refresh data
        refetchEmployees();
        // Update period status
        setPayrollPeriods(prev => prev.map(p =>
            p.month === selectedMonth ? { ...p, processed: true } : p
        ));
    }, [refetchEmployees, selectedMonth]);

    // Handle bulk validation
    const handleBulkValidate = useCallback(async (employeeIds, action) => {
        try {
            await bulkValidate(employeeIds, action);
            setSelectedEmployeeIds([]);
            refetchEmployees();
        } catch (err) {
            console.error('Bulk validation failed:', err);
        }
    }, [bulkValidate, refetchEmployees]);

    // Selection handlers
    const handleSelectEmployee = useCallback((id) => {
        setSelectedEmployeeIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    }, []);

    const handleSelectAll = useCallback(() => {
        const allIds = employees.map(e => e.id);
        setSelectedEmployeeIds(prev =>
            prev.length === allIds.length ? [] : allIds
        );
    }, [employees]);

    const handleClearSelection = useCallback(() => {
        setSelectedEmployeeIds([]);
    }, []);

    // View employee details
    const handleViewEmployee = useCallback((employee) => {
        setViewingEmployee(employee);
        setShowPayslipModal(true);
    }, []);

    // Handle OT config update
    const handleOTConfigChange = useCallback((newConfig) => {
        setOTConfig(newConfig);
    }, []);

    // Handle incentive update for an employee
    const handleIncentiveUpdate = useCallback((employeeId, employeeIncentives) => {
        setIncentives(prev => ({
            ...prev,
            [employeeId]: employeeIncentives
        }));
    }, []);

    // Handle attendance validation complete
    const handleAttendanceValidationComplete = useCallback((decisions) => {
        setAttendanceDecisions(decisions);
        setShowAttendanceValidation(false);
        // Refresh employees with updated attendance data
        refetchEmployees();
    }, [refetchEmployees]);

    // Calculate employee salary with all components
    const calculateEmployeeSalary = useCallback((employee) => {
        const attendanceData = {
            workingDays: workingDaysData?.workingDays || 22,
            lopDays: attendanceDecisions[employee.id]?.lopDays || 0,
            otHours: employee.otHours || { regular: 0, holiday: 0 },
            month: workingDaysData?.month
        };
        const employeeIncentives = incentives[employee.id] || [];
        return calculateNetSalary(employee, attendanceData, employeeIncentives, otConfig);
    }, [workingDaysData, attendanceDecisions, incentives, calculateNetSalary, otConfig]);

    // Get employees with OT hours
    const employeesWithOT = employees.filter(e =>
        (e.otHours?.regular || 0) + (e.otHours?.holiday || 0) > 0
    );

    // Period status badge
    const getStatusBadge = (status) => {
        const configs = {
            current: { bg: 'bg-green-100 text-green-700', label: 'Current' },
            locked: { bg: 'bg-gray-100 text-gray-600', label: 'Locked' },
            upcoming: { bg: 'bg-blue-100 text-blue-700', label: 'Upcoming' }
        };
        const config = configs[status] || configs.upcoming;
        return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg}`}>
                {config.label}
            </span>
        );
    };

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="p-6 max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className={`text-3xl font-bold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Payroll Processing
                            </h1>
                            <p className={`text-sm mt-1 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                                Process monthly payroll for {allEmployees?.length || 0} employees
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {/* Attendance Validation Button */}
                            {selectedPeriod && !selectedPeriod.processed && (
                                <button
                                    onClick={() => setShowAttendanceValidation(true)}
                                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                                        theme === 'dark'
                                            ? 'bg-purple-600 text-white hover:bg-purple-700'
                                            : 'bg-purple-600 text-white hover:bg-purple-700'
                                    }`}
                                >
                                    <Clock className="w-4 h-4" />
                                    Attendance & LOP
                                </button>
                            )}
                            {/* OT/Incentives Button */}
                            {selectedPeriod && !selectedPeriod.processed && (
                                <button
                                    onClick={() => setShowOTPanel(true)}
                                    className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                                        theme === 'dark'
                                            ? 'bg-green-600 text-white hover:bg-green-700'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                >
                                    <Gift className="w-4 h-4" />
                                    OT & Incentives
                                </button>
                            )}
                            <button
                                onClick={() => navigate('/admin/payroll')}
                                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                                    theme === 'dark'
                                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                                }`}
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back
                            </button>
                        </div>
                    </div>
                </div>

                {/* Period Selector */}
                <div className={`rounded-xl shadow-sm p-4 mb-6 ${
                    theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white'
                }`}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <Calendar className={`w-5 h-5 ${
                                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                            }`} />
                            <h2 className={`text-lg font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                Payroll Period
                            </h2>
                        </div>
                        <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className={`px-4 py-2 border rounded-lg ${
                                theme === 'dark'
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            {payrollPeriods.map((period) => (
                                <option key={period.month} value={period.month}>
                                    {period.month} {period.processed ? '(Processed)' : ''}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Period Pills */}
                    <div className="flex gap-2 flex-wrap">
                        {payrollPeriods.slice(-6).map((period) => (
                            <button
                                key={period.month}
                                onClick={() => setSelectedMonth(period.month)}
                                className={`px-3 py-2 rounded-lg text-sm transition-all ${
                                    selectedMonth === period.month
                                        ? 'bg-blue-600 text-white'
                                        : theme === 'dark'
                                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {period.month.split(' ')[0]}
                                {period.processed && (
                                    <Lock className="w-3 h-3 inline ml-1 opacity-70" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Summary Dashboard */}
                {selectedPeriod && !selectedPeriod.processed && (
                    <div className="mb-6">
                        <PayrollSummaryDashboard
                            period={selectedMonth}
                            onCategoryClick={handleCategoryClick}
                            onProcessAll={handleProcessAll}
                            onRefresh={refetchEmployees}
                            isProcessing={isProcessing}
                        />
                    </div>
                )}

                {/* Category View (when a category is selected) */}
                {activeCategory && (
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <button
                                    onClick={() => setActiveCategory(null)}
                                    className={`p-2 rounded-lg ${
                                        theme === 'dark'
                                            ? 'hover:bg-gray-800 text-gray-400'
                                            : 'hover:bg-gray-100 text-gray-600'
                                    }`}
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <h2 className={`text-xl font-bold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    {activeCategory === 'autoValidated' ? 'Auto-Validated Employees' :
                                     activeCategory === 'needsReview' ? 'Employees Needing Review' :
                                     'Employees with Issues'}
                                </h2>
                            </div>
                        </div>

                        {/* Bulk Action Toolbar */}
                        <div className="mb-4">
                            <BulkActionToolbar
                                period={selectedMonth}
                                selectedIds={selectedEmployeeIds}
                                onSelectAll={handleSelectAll}
                                onClearSelection={handleClearSelection}
                                onBulkValidate={handleBulkValidate}
                                totalCount={pagination.total}
                                filteredCount={employees.length}
                                currentFilters={filters}
                                onFilterChange={setFilters}
                                onSearch={setSearch}
                                loading={employeesLoading}
                            />
                        </div>

                        {/* Virtualized Employee List */}
                        <VirtualizedEmployeeList
                            employees={employees}
                            selectedIds={selectedEmployeeIds}
                            onSelectEmployee={handleSelectEmployee}
                            onViewEmployee={handleViewEmployee}
                            onSelectAll={handleSelectAll}
                            loading={employeesLoading}
                        />

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between">
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Page {pagination.page} of {pagination.totalPages}
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => setPage(pagination.page - 1)}
                                        disabled={pagination.page <= 1}
                                        className={`p-2 rounded-lg ${
                                            pagination.page <= 1
                                                ? 'opacity-50 cursor-not-allowed'
                                                : theme === 'dark'
                                                    ? 'hover:bg-gray-800'
                                                    : 'hover:bg-gray-100'
                                        } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => setPage(pagination.page + 1)}
                                        disabled={!pagination.hasMore}
                                        className={`p-2 rounded-lg ${
                                            !pagination.hasMore
                                                ? 'opacity-50 cursor-not-allowed'
                                                : theme === 'dark'
                                                    ? 'hover:bg-gray-800'
                                                    : 'hover:bg-gray-100'
                                        } ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Processed Period View - Show Summary Dashboard in read-only mode */}
                {selectedPeriod?.processed && !activeCategory && (
                    <div className="space-y-6">
                        {/* Locked Banner */}
                        <div className={`rounded-xl p-4 flex items-center gap-4 ${
                            theme === 'dark'
                                ? 'bg-green-900/20 border border-green-800'
                                : 'bg-green-50 border border-green-200'
                        }`}>
                            <Lock className={`w-6 h-6 ${
                                theme === 'dark' ? 'text-green-400' : 'text-green-600'
                            }`} />
                            <div className="flex-1">
                                <h3 className={`font-semibold ${
                                    theme === 'dark' ? 'text-green-400' : 'text-green-700'
                                }`}>
                                    Payroll Processed & Locked
                                </h3>
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-green-300/70' : 'text-green-600'
                                }`}>
                                    {selectedMonth} payroll has been finalized. View the summary below.
                                </p>
                            </div>
                            <button
                                onClick={() => navigate(`/admin/payroll/history?period=${encodeURIComponent(selectedMonth)}`)}
                                className={`px-4 py-2 rounded-lg font-medium ${
                                    theme === 'dark'
                                        ? 'bg-green-800 text-green-200 hover:bg-green-700'
                                        : 'bg-green-600 text-white hover:bg-green-700'
                                }`}
                            >
                                View Payslips
                            </button>
                        </div>

                        {/* Show Summary Dashboard for processed periods (read-only) */}
                        <PayrollSummaryDashboard
                            period={selectedMonth}
                            onCategoryClick={handleCategoryClick}
                            onProcessAll={null}
                            onRefresh={refetchEmployees}
                            isProcessing={false}
                            isLocked={true}
                        />
                    </div>
                )}
            </div>

            {/* Processing Progress Modal */}
            {showProcessingModal && jobId && (
                <ProcessingProgress
                    jobId={jobId}
                    period={selectedMonth}
                    onComplete={handleProcessingComplete}
                    onError={(err) => console.error('Processing error:', err)}
                    onCancel={() => {
                        setShowProcessingModal(false);
                        resetProcessing();
                    }}
                />
            )}

            {/* Payslip Preview Modal */}
            {showPayslipModal && viewingEmployee && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setShowPayslipModal(false)}
                    />
                    <div className={`relative w-full max-w-4xl max-h-[90vh] overflow-auto rounded-xl shadow-2xl ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}>
                        <Payslip
                            employee={viewingEmployee}
                            month={selectedMonth}
                            onClose={() => setShowPayslipModal(false)}
                            otData={otConfig.enabled ? calculateOTPay(viewingEmployee, viewingEmployee.otHours, otConfig) : null}
                            incentives={incentives[viewingEmployee.id] || []}
                            workingDaysData={workingDaysData}
                            attendanceDecisions={attendanceDecisions[viewingEmployee.id]}
                        />
                    </div>
                </div>
            )}

            {/* Attendance Validation Modal */}
            {showAttendanceValidation && (
                <AttendanceValidationModal
                    employees={employees}
                    period={selectedMonth}
                    workingDaysData={workingDaysData}
                    onClose={() => setShowAttendanceValidation(false)}
                    onComplete={handleAttendanceValidationComplete}
                    theme={theme}
                />
            )}

            {/* OT & Incentives Panel */}
            {showOTPanel && (
                <OTIncentivePanel
                    employees={employees}
                    period={selectedMonth}
                    otConfig={otConfig}
                    onOTConfigChange={handleOTConfigChange}
                    incentives={incentives}
                    onIncentiveUpdate={handleIncentiveUpdate}
                    onClose={() => setShowOTPanel(false)}
                    theme={theme}
                />
            )}
        </div>
    );
};

export default PayrollRunEnhanced;
